import { useState, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorBoundary } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Settings } from "lucide-react";
import { ChangelogTable } from "@/components/changelog/changelog-table";
import { ChangelogFilters } from "@/components/changelog/changelog-filters";
import { CategoryManagementDialog } from "@/components/changelog/category-management-dialog";
import {
  changelogEntriesQueryOptions,
  changelogCategoriesQueryOptions,
} from "@/hooks/use-changelog";
import { usePagination } from "@/hooks/use-pagination";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import type { ChangelogEntryFilters } from "@/types/changelog";

export default function ChangelogPage() {
  const navigate = useNavigate();
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [filters, setFiltersState] = useState<ChangelogEntryFilters>({});
  const pagination = usePagination({ initialLimit: 10 });

  const setFilters = (newFilters: ChangelogEntryFilters) => {
    setFiltersState(newFilters);
    pagination.setPage(1);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            체인지로그 관리
          </h1>
          <p className="text-muted-foreground leading-normal">
            서비스 변경사항을 작성하고 발행합니다.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setCategoryDialogOpen(true)}
          >
            <Settings className="mr-2 h-4 w-4" />
            카테고리
          </Button>
          <Button onClick={() => navigate("/changelog/new")}>
            <Plus className="mr-2 h-4 w-4" />
            새 체인지로그
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>체인지로그 목록</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 px-4 md:px-6">
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
                <Suspense fallback={<TableLoadingFallback columnCount={6} />}>
                  <SuspenseQuery {...changelogCategoriesQueryOptions()}>
                    {({ data: categories }) => (
                      <>
                        <ChangelogFilters
                          filters={filters}
                          categories={categories}
                          onFiltersChange={setFilters}
                        />
                        <SuspenseQuery
                          {...changelogEntriesQueryOptions(filters, {
                            page: pagination.page,
                            limit: pagination.limit,
                          })}
                        >
                          {({ data }) => (
                            <ChangelogTable
                              data={data.items}
                              meta={data.meta}
                              pagination={pagination}
                            />
                          )}
                        </SuspenseQuery>
                      </>
                    )}
                  </SuspenseQuery>
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </CardContent>
      </Card>

      <Suspense>
        <CategoryManagementDialog
          open={categoryDialogOpen}
          onOpenChange={setCategoryDialogOpen}
        />
      </Suspense>
    </div>
  );
}
